import React from 'react';
import { useTranslation } from 'react-i18next';
import { GOLD_COLOR, QUEST_STATUSES, QUEST_TYPES, formatPoints } from '../../helpers/quests';
import { QuestIcon } from './QuestIcon';
import { QuestAction } from './QuestAction';
import { HoldDays } from './HoldDays';
import { getQuestColor, getQuestText } from './questText';

export const QuestItem = ({ quest, data, pendingAction, onClaim, onCheckHold }) => {
  const { t } = useTranslation();
  const { title, subtitle } = getQuestText(t, quest, data?.week);
  const color = getQuestColor(quest);
  const isDone = quest.status !== QUEST_STATUSES.ACTIVE;
  const isCombo = quest.effectiveType === QUEST_TYPES.COMBO;
  const showProgress = quest.target > 1 && !isCombo;
  // Gold: полоска до повышенной ступени, отметка на обычной
  const progressMax = quest.stretchTarget || quest.target;
  const isInvite = [QUEST_TYPES.INVITE, QUEST_TYPES.INVITE_MULTI].includes(quest.effectiveType);

  return (
    <div
      className={`flex flex-col space-y-[1.6rem] p-[2rem] dashboard-block-styles ${
        quest.status === QUEST_STATUSES.COMPLETED
          ? '!border-[rgba(81,246,137,0.35)]'
          : quest.isGold
          ? '!border-[rgba(255,194,75,0.25)]'
          : ''
      }`}
    >
      <div className="flex items-center justify-between space-x-[1.6rem] lg:flex-col lg:items-start lg:space-x-0 lg:space-y-[1.6rem]">
        <div className="flex items-center space-x-[1.6rem] min-w-0">
          <div
            className={`flex items-center justify-center shrink-0 w-[5.2rem] h-[5.2rem] rounded-[1.6rem] ${
              quest.isGold ? 'bg-[rgba(255,194,75,0.10)]' : 'bg-white-50'
            }`}
          >
            <QuestIcon type={quest.effectiveType} color={isDone ? '#51F689' : color} />
          </div>
          <div className="flex flex-col space-y-[0.4rem] min-w-0">
            <span className="text-[1.8rem] font-500 text-white leading-normal lg:text-[1.6rem]">{title}</span>
            <span className="text-[1.3rem] text-white-500 leading-normal">{subtitle}</span>
          </div>
        </div>
        <div className="flex items-center space-x-[1.6rem] shrink-0 lg:w-full lg:justify-between">
          <span className="text-[1.8rem] font-600 text-white whitespace-nowrap lg:text-[1.6rem]">
            {quest.stretchPoints && quest.status === QUEST_STATUSES.ACTIVE
              ? t('quests.pointsUpTo', {
                  points: formatPoints(quest.stretchPoints),
                })
              : isInvite && !isDone
              ? t('quests.pointsFrom', { points: formatPoints(quest.points) })
              : `+${formatPoints(quest.points)}`}
            <span className="text-white-500 font-500 text-[1.3rem] ml-[0.4rem]">PP</span>
          </span>
          <QuestAction
            quest={quest}
            data={data}
            pendingAction={pendingAction}
            onClaim={onClaim}
            onCheckHold={onCheckHold}
          />
        </div>
      </div>
      {isCombo && data?.comboParts && (
        <div className="flex flex-wrap gap-[0.8rem]">
          {['partner', 'recycle', 'upgrade'].map((part) => {
            const done = !!data.comboParts[part];
            return (
              <span
                key={part}
                className={`flex items-center h-[3rem] px-[1.2rem] rounded-[1rem] text-[1.3rem] font-500 border-[0.1rem] border-solid ${
                  done
                    ? 'bg-[rgba(81,246,137,0.12)] border-[rgba(81,246,137,0.4)] text-green'
                    : 'border-white-100 text-white-500'
                }`}
              >
                {done ? '✓ ' : ''}
                {t(`quests.items.combo.parts.${part}`, {
                  level: quest.minLevel,
                })}
              </span>
            );
          })}
        </div>
      )}
      {showProgress && (
        <div className="flex flex-col space-y-[1rem]">
          {quest.effectiveType === QUEST_TYPES.HOLD && data?.week && (
            <HoldDays weekStartsAt={data.week.startsAt} checks={data.holdChecks} />
          )}
          <div className="flex items-center space-x-[1.2rem]">
            <div className="relative flex-1 h-[0.6rem] rounded-[2rem] bg-white-100 overflow-hidden">
              <div
                className="h-full rounded-[2rem] custom-transition"
                style={{
                  width: `${Math.min(100, (quest.progress / progressMax) * 100)}%`,
                  background: quest.isGold ? GOLD_COLOR : '#985AFF',
                }}
              />
              {quest.isGold && quest.stretchTarget && (
                <div
                  className="absolute top-0 h-full w-[0.2rem] bg-white-500"
                  style={{
                    left: `${(quest.target / quest.stretchTarget) * 100}%`,
                  }}
                />
              )}
            </div>
            <span className="text-[1.2rem] text-white-500 font-500">
              {quest.progress}/{progressMax}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
